import { useState } from 'react'

import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { ThemeModal } from '../modal'
import { SettingsGroup } from '../SettingsHierarchy'
import { comicSectionLabelClass } from './comicPanelTokens'

/** 与话次画布顶栏按钮同高 */
export const COMIC_EPISODE_CANVAS_ACTION_CLASS = 'h-9 shrink-0 whitespace-nowrap !py-0'

type Props = {
  angular: boolean
  episodeId: string
  title: string
  summary?: string
  plotAdvance?: string
  plannedPageCount?: number
  pageCount: number
  generating: boolean
  platformReady: boolean
  hasWebtoonPages: boolean
  exportBusy: boolean
  exportingEpisode: boolean
  exportingEpisodePdf: boolean
  onOpenWebtoonReader?: () => void
  onExportEpisode?: () => void
  onExportEpisodePdf?: () => void
  onGenerateEpisode?: () => void
}

/** 话次吸顶条内容：标题、操作按钮 */
export function ComicEpisodeCanvasEpisodeHeader({
  angular,
  episodeId,
  title,
  summary,
  plotAdvance,
  plannedPageCount,
  pageCount,
  generating,
  platformReady,
  hasWebtoonPages,
  exportBusy,
  exportingEpisode,
  exportingEpisodePdf,
  onOpenWebtoonReader,
  onExportEpisode,
  onExportEpisodePdf,
  onGenerateEpisode,
}: Props) {
  const [detailOpen, setDetailOpen] = useState(false)
  const metaLabel = [
    `${pageCount} 页`,
    plannedPageCount && plannedPageCount > 0 ? `规划 ${plannedPageCount} 页` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          className="block min-w-0 max-w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
          onClick={() => setDetailOpen(true)}
        >
          <p
            id={`comic-canvas-episode-heading-${episodeId}`}
            className={[comicSectionLabelClass(), 'truncate'].join(' ')}
            title={title}
          >
            {title}
          </p>
        </button>
        <p className="mt-0.5 truncate text-xs tabular-nums text-ink-soft">{metaLabel}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
        {onOpenWebtoonReader && hasWebtoonPages ? (
          <ChromeToolbarButton
            type="button"
            className={COMIC_EPISODE_CANVAS_ACTION_CLASS}
            onClick={onOpenWebtoonReader}
          >
            连续阅读
          </ChromeToolbarButton>
        ) : null}
        {onExportEpisode ? (
          <ChromeToolbarButton
            type="button"
            className={COMIC_EPISODE_CANVAS_ACTION_CLASS}
            disabled={exportBusy}
            onClick={onExportEpisode}
          >
            {exportingEpisode ? '打包中…' : '导出 ZIP'}
          </ChromeToolbarButton>
        ) : null}
        {onExportEpisodePdf ? (
          <ChromeToolbarButton
            type="button"
            className={COMIC_EPISODE_CANVAS_ACTION_CLASS}
            disabled={exportBusy}
            onClick={onExportEpisodePdf}
          >
            {exportingEpisodePdf ? '生成 PDF…' : '导出 PDF'}
          </ChromeToolbarButton>
        ) : null}
        {onGenerateEpisode ? (
          <ChromePrimaryButton
            density="compact"
            type="button"
            className={COMIC_EPISODE_CANVAS_ACTION_CLASS}
            disabled={!platformReady || generating}
            onClick={onGenerateEpisode}
          >
            {generating ? '生成本话…' : '生成本话'}
          </ChromePrimaryButton>
        ) : null}
      </div>
      <ThemeModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="话次详情"
        size="sm"
      >
        <div className="space-y-4">
          <section className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink-soft">标题</p>
              <p className="mt-1 text-sm font-semibold text-ink">{title}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink-soft">分页</p>
              <p className="mt-1 text-sm text-ink">{metaLabel}</p>
            </div>
          </section>
          <SettingsGroup
            angular={angular}
            title="工作流目标"
            description="不参与普通生图提示词设定"
          >
            {summary?.trim() ? (
              <div>
                <p className="text-xs font-semibold text-ink-soft">摘要</p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-ink">{summary.trim()}</p>
              </div>
            ) : null}
            {plotAdvance?.trim() ? (
              <div>
                <p className="text-xs font-semibold text-ink-soft">剧情推进</p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-ink">{plotAdvance.trim()}</p>
              </div>
            ) : null}
          </SettingsGroup>
        </div>
      </ThemeModal>
    </>
  )
}
